// Single source of truth for every real, crawlable URL on cre8now.design.
//
// Imported by BOTH App.jsx (client-side routing) and scripts/prerender.js
// (build-time static HTML generation). Keeping them in one file is the whole
// point: a route that exists here gets prerendered, listed in the sitemap and
// resolved by the SPA. A route that only existed in App.jsx would be invisible
// to Google; one that only existed in the prerenderer would 404 on client-side
// navigation. Add routes here and nowhere else.
//
// Plain JS with no imports on purpose — Node runs this file directly during the
// build, outside Vite.

// Locale-prefixed SEO solution subpages: pathname -> { view, lang }.
// Each renders SolutionPageTemplate with that view's CONTENT config, which
// owns its own title/description/canonical/hreflang/JSON-LD.
export const SEO_SUBPAGE_ROUTES = {
  '/de/design/ux-design': { view: 'ux-design', lang: 'de' },
  '/en/design/ux-design': { view: 'ux-design', lang: 'en' },
  '/de/design/consulting': { view: 'consulting', lang: 'de' },
  '/en/design/consulting': { view: 'consulting', lang: 'en' },
  '/de/design/ui-design': { view: 'ui-design', lang: 'de' },
  '/en/design/ui-design': { view: 'ui-design', lang: 'en' },
  '/de/design/ix-design': { view: 'ix-design', lang: 'de' },
  '/en/design/ix-design': { view: 'ix-design', lang: 'en' },
  '/de/design/strategy': { view: 'strategy', lang: 'de' },
  '/en/design/strategy': { view: 'strategy', lang: 'en' },
  '/de/design/prototyping': { view: 'prototyping', lang: 'de' },
  '/en/design/prototyping': { view: 'prototyping', lang: 'en' },
  '/de/design/seo': { view: 'seo', lang: 'de' },
  '/en/design/seo': { view: 'seo', lang: 'en' },
  '/de/design/development': { view: 'development', lang: 'de' },
  '/en/design/development': { view: 'development', lang: 'en' },
  '/de/design/social-ads': { view: 'social-ads', lang: 'de' },
  '/en/design/social-ads': { view: 'social-ads', lang: 'en' },
  '/de/design/trainings': { view: 'trainings', lang: 'de' },
  '/en/design/trainings': { view: 'trainings', lang: 'en' },
  '/de/ai/ai-consulting': { view: 'ai-consulting', lang: 'de' },
  '/en/ai/ai-consulting': { view: 'ai-consulting', lang: 'en' },
  '/de/ai/ai-agents': { view: 'ai-agents', lang: 'de' },
  '/en/ai/ai-agents': { view: 'ai-agents', lang: 'en' },
  '/de/ai/digital-concierge': { view: 'digital-concierge', lang: 'de' },
  '/en/ai/digital-concierge': { view: 'digital-concierge', lang: 'en' },
  '/de/ai/ai-pipelines': { view: 'ai-pipelines', lang: 'de' },
  '/en/ai/ai-pipelines': { view: 'ai-pipelines', lang: 'en' },
  '/de/ai/ai-content': { view: 'ai-content', lang: 'de' },
  '/en/ai/ai-content': { view: 'ai-content', lang: 'en' },
  '/de/ai/ai-workshops': { view: 'ai-workshops', lang: 'de' },
  '/en/ai/ai-workshops': { view: 'ai-workshops', lang: 'en' },
  '/de/ai/ai-film-production': { view: 'ai-film-production', lang: 'de' },
  '/en/ai/ai-film-production': { view: 'ai-film-production', lang: 'en' },
  '/de/ai/custom-ai-development': { view: 'custom-ai-development', lang: 'de' },
  '/en/ai/custom-ai-development': { view: 'custom-ai-development', lang: 'en' },
  '/de/ai/ai-voice-cloning': { view: 'ai-voice-cloning', lang: 'de' },
  '/en/ai/ai-voice-cloning': { view: 'ai-voice-cloning', lang: 'en' },
  // Case studies — own page component, same self-managed head as the
  // solution pages (title, canonical, hreflang, JSON-LD).
  '/de/case-studies/minimal-bar': { view: 'case-minimal-bar', lang: 'de' },
  '/en/case-studies/minimal-bar': { view: 'case-minimal-bar', lang: 'en' },
};

// Real, lang-prefixed paths for the main Design/AI/CRE8GUIDE/Concierge/
// Prototyping tabs, CRE8APPS and Impressum — replaces the old ?tab=/?v=
// query-param scheme that Google Search Console couldn't index (everything
// canonicalized down to "/"). `tab` routes render the home LandingPage with
// that tab active (or, for concierge, the standalone campaign view); routes
// without `tab` (CRE8APPS, Impressum) are distinct views App.jsx manages
// canonical/hreflang for directly, keyed off `slug`.
export const MAIN_TAB_ROUTES = {
  '/de/design': { view: 'home', tab: 'design', lang: 'de', slug: 'design' },
  '/en/design': { view: 'home', tab: 'design', lang: 'en', slug: 'design' },
  '/de/ai': { view: 'home', tab: 'ai', lang: 'de', slug: 'ai' },
  '/en/ai': { view: 'home', tab: 'ai', lang: 'en', slug: 'ai' },
  '/de/cre8guide': { view: 'home', tab: 'cre8guide', lang: 'de', slug: 'cre8guide' },
  '/en/cre8guide': { view: 'home', tab: 'cre8guide', lang: 'en', slug: 'cre8guide' },
  '/de/concierge': { view: 'campaign-ai-voice-agent', tab: 'concierge', lang: 'de', slug: 'concierge' },
  '/en/concierge': { view: 'campaign-ai-voice-agent', tab: 'concierge', lang: 'en', slug: 'concierge' },
  '/de/prototyping': { view: 'home', tab: 'prototyping', lang: 'de', slug: 'prototyping' },
  '/en/prototyping': { view: 'home', tab: 'prototyping', lang: 'en', slug: 'prototyping' },
  '/de/cre8apps': { view: 'cre8apps', lang: 'de', slug: 'cre8apps' },
  '/en/cre8apps': { view: 'cre8apps', lang: 'en', slug: 'cre8apps' },
  '/de/impressum': { view: 'impressum', lang: 'de', slug: 'impressum' },
  '/en/impressum': { view: 'impressum', lang: 'en', slug: 'impressum' },
};

// Bare, unprefixed paths that were indexed under the old scheme. They are NOT
// routes any more and are NOT prerendered — Apache 301s them to their German
// equivalent (see php/.htaccess), which consolidates their existing ranking
// signals onto the canonical URL instead of splitting them across a duplicate.
// App.jsx still resolves them client-side as a safety net for anyone who
// bookmarked one and reaches it without hitting Apache (e.g. `npm run dev`).
export const LEGACY_ALIASES = {
  '/cre8guide': '/de/cre8guide',
  '/concierge': '/de/concierge',
  '/prototyping': '/de/prototyping',
  '/cre8apps': '/de/cre8apps',
};

// The homepage serves whichever language the visitor's browser or stored
// preference implies, so it is the hreflang x-default of the cluster whose
// localized members are /de/design and /en/design (same content, pinned
// language). Declared here so index.html, the prerenderer and the sitemap all
// emit the identical, reciprocal set.
export const HOME_HREFLANG = {
  'x-default': '/',
  de: '/de/design',
  en: '/en/design',
};

// The other language's URL for a given pathname, or null if there isn't one.
//
// Exists because every page used to link only to its own language's siblings,
// and the language switcher navigates via window.location.href — which Google
// does not follow. All 26 German pages therefore had zero inbound <a> links
// and were reachable only through the sitemap, earning no internal link equity
// at all. Footers render this as a real anchor so each page links to its
// counterpart.
//
// The bare homepage is the x-default of the /de|en/design cluster, so its
// counterparts come from HOME_HREFLANG rather than from a path swap.
export const alternateLangPath = (pathname, targetLang) => {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path === '/') return HOME_HREFLANG[targetLang] ?? null;
  const other = targetLang === 'de' ? '/de/' : '/en/';
  if (path.startsWith('/de/') || path.startsWith('/en/')) {
    return other + path.slice(4);
  }
  return null;
};

// Title + description for the views that own neither LandingPage's per-tab
// metadata nor SolutionPageTemplate's CONTENT config. Without this, CRE8APPS
// and Impressum inherited whatever the LandingPage rendering behind them had
// last set — Impressum was shipping the Rapid-Prototyping title, which reads
// to Google as a duplicate of a completely unrelated page.
export const PAGE_META = {
  cre8apps: {
    de: {
      title: 'CRE8NOW | CRE8APPS – KI-Tools & Apps aus Berlin',
      description:
        'CRE8APPS ist die Tool-Sammlung von CRE8NOW: KI-gestützte Apps und Helfer für Design-, Content- und Automatisierungs-Workflows. Entwickelt in Berlin von einer Design- & AI-Agentur mit 25+ Jahren Erfahrung.',
    },
    en: {
      title: 'CRE8NOW | CRE8APPS – AI Tools & Apps from Berlin',
      description:
        'CRE8APPS is the CRE8NOW tool collection: AI-powered apps and helpers for design, content and automation workflows. Built in Berlin by a design & AI agency with 25+ years of experience.',
    },
  },
  impressum: {
    de: {
      title: 'CRE8NOW | Impressum & Datenschutz',
      description:
        'Impressum, Anbieterkennzeichnung und Datenschutzhinweise der CRE8NOW Design- & AI-Agentur aus Berlin.',
    },
    en: {
      title: 'CRE8NOW | Legal Notice & Privacy',
      description:
        'Legal notice, company details and privacy information for CRE8NOW, the design & AI agency based in Berlin.',
    },
  },
};

// Views whose metadata PAGE_META owns. LandingPage.jsx renders behind these
// views and must not overwrite their title/description with its per-tab set.
export const PAGE_META_VIEWS = Object.keys(PAGE_META);

// The CRE8GUIDE hub (cre8guide-0) plus its 24 articles. These are Webflow
// exports that live on the server only — they are NOT in this repo and NOT in
// the build output, so a deploy must ADD to the server, never mirror-with-delete.
//
// Listed WITHOUT the .html extension on purpose: every one of these pages
// declares <link rel="canonical" href="https://cre8now.design/cre8guide-N">,
// extensionless. Under the old SPA catch-all that URL returned HTTP 200 with
// the homepage, so all 25 articles pointed Google's canonical at the homepage
// and none of them could rank on its own. php/.htaccess now serves
// /cre8guide-N from /cre8guide-N.html and 301s the .html spelling onto it, so
// the URL the pages claim is the URL that answers.
export const STATIC_ARTICLE_PATHS = Array.from(
  { length: 25 },
  (_, i) => `/cre8guide-${i}`
);

// Every SPA route that gets its own prerendered index.html, homepage first.
export const PRERENDER_PATHS = [
  '/',
  ...Object.keys(MAIN_TAB_ROUTES),
  ...Object.keys(SEO_SUBPAGE_ROUTES),
];
